import React, { useState } from 'react';
import { PlusOutlined, CameraOutlined, DeleteOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { Modal, message } from 'antd';
import api from '../../api/axios';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/* ─── shared styles ─────────────────────────────────────── */
const BASE_BOX = {
  borderRadius: 12,
  overflow: 'hidden',
  cursor: 'pointer',
  position: 'relative',
  background: 'var(--qk-bg-soft)',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

/* ══════════════════════════════════════════════════════════
   1. SingleImageUpload
   Props:
     file     : string | null      — server URL (from setFile response)
     setFile  : (data) => void     — called with API response on success
     width    : number | string    (default 200)
     height   : number | string    (default 200)
     accept   : string
     disabled : boolean
   API: POST /api/uploads/single  { file: File }
════════════════════════════════════════════════════════════ */
export function SingleImageUpload({
  file = null,
  setFile,
  width = 200,
  height = 200,
  accept = ACCEPT,
  disabled = false,
}) {
  const [hover, setHover] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localUrl, setLocalUrl] = useState(null);
  const [preview, setPreview] = useState(false);

  const previewUrl = file || localUrl;

  const handleClick = () => {
    if (disabled || loading) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = async (e) => {
      const picked = e.target.files?.[0];
      if (!picked) return;

      setLocalUrl(URL.createObjectURL(picked));
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append('file', picked);
        const res = await api.post('/upload', fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setFile?.(res.data.data.file_path);
        setLocalUrl(null);
      } catch {
        message.error('Rasm yuklanmadi, qayta urinib ko\'ring');
        setLocalUrl(null);
      } finally {
        setLoading(false);
      }
    };
    input.click();
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setFile?.(null);
    setLocalUrl(null);
  };

  return (
    <>
      <div
        onClick={handleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          ...BASE_BOX,
          width,
          height,
          border: `2px dashed ${hover && !disabled ? '#667eea' : '#d9d9d9'}`,
          boxShadow: hover && !disabled ? '0 0 0 3px rgba(102,126,234,0.12)' : 'none',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {previewUrl ? (
          <>
            <img
              src={process.env.REACT_APP_URL + previewUrl}
              alt="preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {loading && (
              <div style={{ ...overlay, flexDirection: 'column', gap: 8 }}>
                <LoadingOutlined style={{ fontSize: 28, color: '#fff' }} />
                <span style={{ color: '#fff', fontSize: 12 }}>Yuklanmoqda...</span>
              </div>
            )}
            {hover && !loading && !disabled && (
              <div style={overlay}>
                <button
                  onClick={(e) => { e.stopPropagation(); setPreview(true); }}
                  style={iconBtn}
                  title="Ko'rish"
                >
                  <EyeOutlined style={{ fontSize: 18, color: '#fff' }} />
                </button>
                <button onClick={handleRemove} style={iconBtn} title="O'chirish">
                  <DeleteOutlined style={{ fontSize: 18, color: '#ff4d4f' }} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={emptyCenter}>
            {loading ? (
              <LoadingOutlined style={{ fontSize: 26, color: '#667eea' }} />
            ) : (
              <>
                <div style={iconCircle('#667eea')}>
                  <CameraOutlined style={{ fontSize: 22, color: '#667eea' }} />
                </div>
                <span style={{ fontSize: 13, color: '#888', marginTop: 8 }}>Rasm yuklang</span>
                <span style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>PNG, JPG, WEBP</span>
              </>
            )}
          </div>
        )}
      </div>

      <Modal open={preview} footer={null} onCancel={() => setPreview(false)} centered>
        <img src={process.env.REACT_APP_URL + previewUrl} alt="full" style={{ width: '100%', borderRadius: 8 }} />
      </Modal>
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   2. MultiImageUpload
   Props:
     file     : any[]           — server responses array
     setFile  : (data[]) => void
     max      : number          (default 10)
     itemSize : number          (default 110)
     accept   : string
     disabled : boolean
   API: POST /api/uploads/multiple  { files: File[] }
════════════════════════════════════════════════════════════ */
export function MultiImageUpload({
  file = [],
  setFile,
  max = 10,
  itemSize = 110,
  accept = ACCEPT,
  disabled = false,
}) {
  const [previewSrc, setPreviewSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  const getUrl = (item) =>
    typeof item === 'string' ? item : item?.url || item?.path || null;

  const handleAdd = () => {
    if (disabled || loading || file.length >= max) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = true;
    input.onchange = async (e) => {
      const picked = Array.from(e.target.files || []);
      if (!picked.length) return;
      const take = picked.slice(0, max - file.length);

      setLoading(true);
      try {
        const fd = new FormData();
        take.forEach((f) => fd.append('file', f));
        const res = await api.post('/upload', fd);
        const uploaded = Array.isArray(res.data) ? res.data : [res.data];
        setFile?.([...file, ...uploaded]);
      } catch {
        message.error('Rasmlar yuklanmadi, qayta urinib ko\'ring');
      } finally {
        setLoading(false);
      }
    };
    input.click();
  };

  const handleRemove = (idx, e) => {
    e.stopPropagation();
    setFile?.(file.filter((_, i) => i !== idx));
  };

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {file.map((item, idx) => (
          <ImageThumb
            key={idx}
            src={getUrl(item)}
            size={itemSize}
            disabled={disabled}
            onRemove={(e) => handleRemove(idx, e)}
            onPreview={() => setPreviewSrc(getUrl(item))}
          />
        ))}

        {file.length < max && !disabled && (
          <AddButton size={itemSize} loading={loading} onClick={handleAdd} />
        )}
      </div>

      <Modal open={!!previewSrc} footer={null} onCancel={() => setPreviewSrc(null)} centered>
        {previewSrc && (
          <img src={previewSrc} alt="full" style={{ width: '100%', borderRadius: 8 }} />
        )}
      </Modal>
    </>
  );
}

/* ─── sub-components ────────────────────────────────────── */
function ImageThumb({ src, size, disabled, onRemove, onPreview }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...BASE_BOX, width: size, height: size, border: '1.5px solid #e0e0e0', cursor: 'default', flexShrink: 0 }}
    >
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {hover && !disabled && (
        <div style={overlay}>
          <button onClick={onPreview} style={iconBtn} title="Ko'rish">
            <EyeOutlined style={{ fontSize: 16, color: '#fff' }} />
          </button>
          <button onClick={onRemove} style={iconBtn} title="O'chirish">
            <DeleteOutlined style={{ fontSize: 16, color: '#ff4d4f' }} />
          </button>
        </div>
      )}
    </div>
  );
}

function AddButton({ size, loading, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...BASE_BOX,
        width: size,
        height: size,
        border: `2px dashed ${hover && !loading ? '#667eea' : '#d9d9d9'}`,
        boxShadow: hover && !loading ? '0 0 0 3px rgba(102,126,234,0.12)' : 'none',
        flexShrink: 0,
        cursor: loading ? 'not-allowed' : 'pointer',
      }}
    >
      <div style={emptyCenter}>
        {loading ? (
          <LoadingOutlined style={{ fontSize: 22, color: '#667eea' }} />
        ) : (
          <>
            <div style={iconCircle(hover ? '#667eea' : '#bbb')}>
              <PlusOutlined style={{ fontSize: 18, color: hover ? '#667eea' : '#bbb' }} />
            </div>
            <span style={{ fontSize: 11, color: '#bbb', marginTop: 6 }}>Rasm qo'shish</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── shared micro styles ────────────────────────────────── */
const emptyCenter = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
};

const overlay = {
  position: 'absolute', inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
};

const iconBtn = {
  background: 'rgba(255,255,255,0.15)',
  border: 'none', borderRadius: 6,
  width: 32, height: 32,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', padding: 0,
};

function iconCircle(color) {
  return {
    width: 44, height: 44, borderRadius: '50%',
    background: color + '18',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
